import Button from "components/Button/Button";
import ButtonIcon from "components/ButtonIcon/ButtonIcon";
import Card from "components/Card/Card";
import FutureMedicalCard from "components/Card/FutureMedicalCard";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { DatePicker, MaskedInput, Select, TextInput } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useCallback, useState } from "react";
import { useFormContext } from "react-hook-form";
import {
  useDeletePartCDPlanCoverageMutation,
  usePatchPartCDPlanCoverageMutation,
} from "services/part-coverage-api";
import type { PartCDPlanCoverageType } from "types/DataTypes";
import {
  DATE_TO_STRING,
  getTimeFromISOString,
  STRING_TO_DATE,
} from "utils/date-utils";
import { useConstants } from "utils/hooks";

import type { HistoricalDates, PartCDFormProps } from "../types";

export const PartCDForm = ({ coverage, isComplete }: PartCDFormProps) => {
  const { constants } = useConstants();
  const { clearErrors } = useFormContext();
  const [patchPartCDCoverage] = usePatchPartCDPlanCoverageMutation();
  const [deletePartCDCoverage] = useDeletePartCDPlanCoverageMutation();
  const [coverageToDelete, setCoverageToDelete] =
    useState<PartCDPlanCoverageType | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [coverageToAddHistory, setCoverageToAddHistory] =
    useState<PartCDPlanCoverageType | null>(null);
  const [historicalDates, setHistoricalDates] = useState<HistoricalDates>({
    effective_date: null,
    termination_date: null,
  });

  const handleOnSave = useCallback(
    async (field: string, value: unknown) => {
      if (!coverage.id) return;

      // Validate date ranges
      if (field === "termination_date" && value instanceof Date) {
        const effectiveDate = STRING_TO_DATE(coverage.effective_date);
        if (effectiveDate && value < effectiveDate) {
          KaboodleToaster.error(
            "Termination date cannot be before effective date",
          );
          return;
        }
      }

      if (field === "effective_date" && value instanceof Date) {
        const terminationDate = STRING_TO_DATE(coverage.termination_date);
        if (terminationDate && value > terminationDate) {
          KaboodleToaster.error(
            "Effective date cannot be after termination date",
          );
          return;
        }
      }

      const formattedValue =
        value instanceof Date ? DATE_TO_STRING(value) : value;

      try {
        await patchPartCDCoverage({
          id: String(coverage.id),
          data: { [field]: formattedValue },
        }).unwrap();
        clearErrors(field);
      } catch (error) {
        if (error.data) {
          Object.entries(error.data).forEach(([, messages]) => {
            const errorMessage = Array.isArray(messages)
              ? messages[0]
              : messages;
            KaboodleToaster.error(errorMessage);
          });
        } else {
          KaboodleToaster.error("Failed to save changes");
        }
      }
    },
    [
      coverage.id,
      coverage.effective_date,
      coverage.termination_date,
      patchPartCDCoverage,
      clearErrors,
    ],
  );

  const handleDeleteCoverage = useCallback(async () => {
    if (!coverageToDelete) return;

    try {
      await deletePartCDCoverage({
        id: coverageToDelete.id,
      }).unwrap();
      setCoverageToDelete(null);
    } catch (error) {
      console.error("Failed to delete coverage:", error);
    }
  }, [coverageToDelete, deletePartCDCoverage]);

  const handleChange = useCallback((id: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [id]: value,
    }));
  }, []);

  const handleBlur = useCallback(
    (field: string, id: string) => {
      const value = formValues[id];
      if (value !== undefined) {
        handleOnSave(field, value);
      }
    },
    [formValues, handleOnSave],
  );

  const handleAddHistory = async (dates: {
    effective_date: Date;
    termination_date: Date;
  }) => {
    if (!coverageToAddHistory) return;

    if (dates.effective_date > dates.termination_date) {
      KaboodleToaster.error(
        "Effective date must be less than or equal to termination date",
      );
      return;
    }

    try {
      await patchPartCDCoverage({
        id: String(coverageToAddHistory.id),
        data: {
          historical: [
            ...coverageToAddHistory.historical,
            {
              effective_date: DATE_TO_STRING(dates.effective_date),
              termination_date: DATE_TO_STRING(dates.termination_date),
            },
          ],
        },
      }).unwrap();
      setCoverageToAddHistory(null);
      setHistoricalDates({ effective_date: null, termination_date: null });
      KaboodleToaster.success("Historical coverage added successfully");
    } catch (error) {
      console.error("Failed to add historical coverage:", error);
      const errorMessage =
        error.data?.historical?.[0] || "Failed to add historical coverage";
      KaboodleToaster.error(errorMessage);
    }
  };

  const getFormattedDate = (date: string | null) => {
    if (!date) return "";
    return getTimeFromISOString(date, "dateTime");
  };

  return (
    <>
      <div className="coverage-form">
        <FutureMedicalCard
          heading={`Part ${coverage.type}${
            coverage.created ? ` - ${getFormattedDate(coverage.created)}` : ""
          }`}
          headerButton={
            !isComplete ? (
              <div className="flex jc-end">
                <ButtonIcon
                  kind="neutral"
                  onClick={() => setCoverageToAddHistory(coverage)}
                  className="header-action"
                >
                  history
                </ButtonIcon>
                <ButtonIcon
                  kind="neutral"
                  onClick={() => setCoverageToDelete(coverage)}
                  className="header-action delete"
                >
                  delete
                </ButtonIcon>
              </div>
            ) : undefined
          }
        >
          <div>
            <label htmlFor={`${coverage.id}-contract-number`}>
              Contract Number
            </label>
            <div style={{ width: "100%" }}>
              <TextInput
                name={`${coverage.id}-contract-number`}
                placeholder=""
                value={
                  formValues[`${coverage.id}-contract-number`] ??
                  coverage.contract_number ??
                  ""
                }
                onChange={(e) =>
                  handleChange(`${coverage.id}-contract-number`, e.target.value)
                }
                onBlur={() =>
                  handleBlur(
                    "contract_number",
                    `${coverage.id}-contract-number`,
                  )
                }
                readOnly={isComplete}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${coverage.id}-pbp-number`}>PBP Number</label>
            <div style={{ width: "100%" }}>
              <TextInput
                name={`${coverage.id}-pbp-number`}
                placeholder=""
                value={
                  formValues[`${coverage.id}-pbp-number`] ??
                  coverage.pbp_number ??
                  ""
                }
                onChange={(e) =>
                  handleChange(`${coverage.id}-pbp-number`, e.target.value)
                }
                onBlur={() =>
                  handleBlur("pbp_number", `${coverage.id}-pbp-number`)
                }
                readOnly={isComplete}
              />
            </div>
          </div>

          <div>
            <label>Effective Date</label>
            <div style={{ width: "100%" }}>
              <DatePicker
                name={`${coverage.id}-effective-date`}
                initialDate={STRING_TO_DATE(coverage.effective_date)}
                onDateChange={(value) => handleOnSave("effective_date", value)}
                readOnly={isComplete}
                allowFutureDates
              />
            </div>
          </div>

          <div>
            <label>Termination Date</label>
            <div style={{ width: "100%" }}>
              <DatePicker
                name={`${coverage.id}-termination-date`}
                initialDate={STRING_TO_DATE(coverage.termination_date)}
                onDateChange={(value) =>
                  handleOnSave("termination_date", value)
                }
                readOnly={isComplete}
                allowFutureDates
                minDate={STRING_TO_DATE(coverage.effective_date) || undefined}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${coverage.id}-contract-name`}>
              Contract Name
            </label>
            <div style={{ width: "100%" }}>
              <TextInput
                name={`${coverage.id}-contract-name`}
                placeholder=""
                value={
                  formValues[`${coverage.id}-contract-name`] ??
                  coverage.contract_name ??
                  ""
                }
                onChange={(e) =>
                  handleChange(`${coverage.id}-contract-name`, e.target.value)
                }
                onBlur={() =>
                  handleBlur("contract_name", `${coverage.id}-contract-name`)
                }
                readOnly={isComplete}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${coverage.id}-address1`}>Address</label>
            <div style={{ width: "100%" }}>
              <TextInput
                name={`${coverage.id}-address1`}
                placeholder=""
                value={
                  formValues[`${coverage.id}-address1`] ??
                  coverage.address1 ??
                  ""
                }
                onChange={(e) =>
                  handleChange(`${coverage.id}-address1`, e.target.value)
                }
                onBlur={() => handleBlur("address1", `${coverage.id}-address1`)}
                readOnly={isComplete}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${coverage.id}-address2`}>Address 2</label>
            <div style={{ width: "100%" }}>
              <TextInput
                name={`${coverage.id}-address2`}
                placeholder=""
                value={
                  formValues[`${coverage.id}-address2`] ??
                  coverage.address2 ??
                  ""
                }
                onChange={(e) =>
                  handleChange(`${coverage.id}-address2`, e.target.value)
                }
                onBlur={() => handleBlur("address2", `${coverage.id}-address2`)}
                readOnly={isComplete}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${coverage.id}-city`}>City</label>
            <div style={{ width: "100%" }}>
              <TextInput
                name={`${coverage.id}-city`}
                placeholder=""
                value={formValues[`${coverage.id}-city`] ?? coverage.city ?? ""}
                onChange={(e) =>
                  handleChange(`${coverage.id}-city`, e.target.value)
                }
                onBlur={() => handleBlur("city", `${coverage.id}-city`)}
                readOnly={isComplete}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${coverage.id}-state`}>State</label>
            <div style={{ width: "100%" }}>
              <Select
                name={`${coverage.id}-state`}
                placeholder="Select State"
                value={
                  coverage.state
                    ? { value: coverage.state, label: coverage.state }
                    : null
                }
                onChange={(option: OptionType | null) => {
                  if (option?.value) {
                    handleOnSave("state", option.value.toString());
                  }
                }}
                options={constants?.STATES || []}
                readOnly={isComplete}
              />
            </div>
          </div>

          <div>
            <label htmlFor={`${coverage.id}-zip`}>Zip</label>
            <div style={{ width: "100%" }}>
              <MaskedInput
                mask="zipcode"
                name={`${coverage.id}-zip`}
                placeholder=""
                value={formValues[`${coverage.id}-zip`] ?? coverage.zip ?? ""}
                onChange={(e) =>
                  handleChange(`${coverage.id}-zip`, e.target.value)
                }
                onBlur={() => handleBlur("zip", `${coverage.id}-zip`)}
                readOnly={isComplete}
              />
            </div>
          </div>
        </FutureMedicalCard>
      </div>

      {coverageToDelete && (
        <KaboodleModal
          isOpen={!!coverageToDelete}
          onClose={() => setCoverageToDelete(null)}
          footer={
            <div className="flex jc-c">
              <Button onClick={handleDeleteCoverage}>Delete</Button>
              <Button onClick={() => setCoverageToDelete(null)}>Cancel</Button>
            </div>
          }
        >
          <Card heading="Delete Coverage">
            <p>Are you sure you want to delete this coverage?</p>
          </Card>
        </KaboodleModal>
      )}

      {coverageToAddHistory && (
        <KaboodleModal
          isOpen={!!coverageToAddHistory}
          onClose={() => {
            setCoverageToAddHistory(null);
            setHistoricalDates({
              effective_date: null,
              termination_date: null,
            });
          }}
          footer={
            <div className="flex jc-c gap-2">
              <Button
                onClick={() => {
                  if (
                    historicalDates.effective_date &&
                    historicalDates.termination_date
                  ) {
                    handleAddHistory({
                      effective_date: historicalDates.effective_date,
                      termination_date: historicalDates.termination_date,
                    });
                  }
                }}
                disabled={
                  !historicalDates.effective_date ||
                  !historicalDates.termination_date
                }
              >
                Save
              </Button>
              <Button
                onClick={() => {
                  setCoverageToAddHistory(null);
                  setHistoricalDates({
                    effective_date: null,
                    termination_date: null,
                  });
                }}
              >
                Cancel
              </Button>
            </div>
          }
        >
          <Card
            heading="Add Historical Coverage"
            style={{ maxHeight: "500px", overflowY: "auto" }}
          >
            <div>
              <label>Historical Effective Date</label>
              <div style={{ width: "100%" }}>
                <DatePicker
                  name="historical-effective-date"
                  onDateChange={(value) =>
                    setHistoricalDates((prev) => ({
                      ...prev,
                      effective_date: value,
                    }))
                  }
                  allowFutureDates
                  initialDate={historicalDates.effective_date}
                />
              </div>
            </div>
            <div>
              <label>Historical Termination Date</label>
              <div style={{ width: "100%" }}>
                <DatePicker
                  name="historical-termination-date"
                  onDateChange={(value) =>
                    setHistoricalDates((prev) => ({
                      ...prev,
                      termination_date: value,
                    }))
                  }
                  allowFutureDates
                  initialDate={historicalDates.termination_date}
                  minDate={historicalDates.effective_date || undefined}
                />
              </div>
            </div>
            <div
              className="border-b border-gray-200 my-8"
              style={{ marginBottom: "8px" }}
            />
            {coverageToAddHistory.historical
              .slice()
              .sort(
                (a, b) =>
                  new Date(a.effective_date).getTime() -
                  new Date(b.effective_date).getTime(),
              )
              .reverse()
              .map((record, index) => (
                <div key={index}>
                  <div>
                    <label>Effective Date</label>
                    <div style={{ width: "100%" }}>
                      <DatePicker
                        name={`historical-effective-date-${index}`}
                        initialDate={STRING_TO_DATE(record.effective_date)}
                        readOnly
                        onDateChange={() => {}}
                      />
                    </div>
                  </div>
                  <div>
                    <label>Termination Date</label>
                    <div style={{ width: "100%" }}>
                      <DatePicker
                        name={`historical-termination-date-${index}`}
                        initialDate={STRING_TO_DATE(record.termination_date)}
                        readOnly
                        onDateChange={() => {}}
                      />
                    </div>
                  </div>
                  {index < coverageToAddHistory.historical.length - 1 && (
                    <div
                      className="border-b border-gray-200"
                      style={{ marginBottom: "8px" }}
                    />
                  )}
                </div>
              ))}
          </Card>
        </KaboodleModal>
      )}
    </>
  );
};
